import type { Column, Section, Widget } from "@emkit/builder-core";
import {
  useEditor,
  useSelectedColumnId,
  useSelectedSectionId,
  useSelectedWidgetId,
} from "../store.js";
import { AdvancedFields } from "./inspectors/AdvancedFields.js";
import { ColumnInspector } from "./inspectors/ColumnInspector.js";
import { SectionInspector } from "./inspectors/SectionInspector.js";
import { inspectorFor } from "./inspectors/registry.js";

interface WidgetLocation {
  widget: Widget;
  columnIndex: number;
  widgetIndex: number;
  columnCount: number;
}

function findWidgetLocation(
  page: ReturnType<typeof useEditor.getState>["page"],
  id: string,
): WidgetLocation | null {
  for (const s of page.sections) {
    for (let ci = 0; ci < s.columns.length; ci++) {
      const col = s.columns[ci];
      if (!col) continue;
      const wi = col.widgets.findIndex((w) => w.id === id);
      if (wi !== -1) {
        const widget = col.widgets[wi];
        if (!widget) continue;
        return {
          widget,
          columnIndex: ci,
          widgetIndex: wi,
          columnCount: col.widgets.length,
        };
      }
    }
  }
  return null;
}

function findSection(
  page: ReturnType<typeof useEditor.getState>["page"],
  id: string,
): Section | null {
  return page.sections.find((s) => s.id === id) ?? null;
}

function findColumn(
  page: ReturnType<typeof useEditor.getState>["page"],
  id: string,
): { section: Section; column: Column; index: number } | null {
  for (const s of page.sections) {
    const idx = s.columns.findIndex((c) => c.id === id);
    if (idx !== -1) {
      const col = s.columns[idx];
      if (col) return { section: s, column: col, index: idx };
    }
  }
  return null;
}

export function Inspector() {
  const page = useEditor((s) => s.page);
  const selection = useEditor((s) => s.selection);
  const selectedWidgetId = useSelectedWidgetId();
  const selectedSectionId = useSelectedSectionId();
  const selectedColumnId = useSelectedColumnId();

  if (!selection) {
    return (
      <aside className="emke-inspector">
        <div className="emke-section-title">Inspector</div>
        <div className="emke-empty">
          Click a widget, column or section on the canvas to edit it.
          <br />
          <br />
          <span style={{ color: "var(--fg-dim)", fontSize: 11, lineHeight: 1.6 }}>
            Shortcuts: Del remove · Ctrl+Z undo ·
            <br />
            Ctrl+C/V copy/paste · Ctrl+D duplicate · Esc deselect
          </span>
        </div>
      </aside>
    );
  }

  if (selectedWidgetId) {
    const loc = findWidgetLocation(page, selectedWidgetId);
    if (!loc) return <MissingInspector what="widget" />;
    return <WidgetInspectorPanel loc={loc} />;
  }

  if (selectedSectionId) {
    const section = findSection(page, selectedSectionId);
    if (!section) return <MissingInspector what="section" />;
    return <SectionInspector section={section} />;
  }

  if (selectedColumnId) {
    const found = findColumn(page, selectedColumnId);
    if (!found) return <MissingInspector what="column" />;
    return (
      <ColumnInspector
        column={found.column}
        sectionId={found.section.id}
        sectionColumnCount={found.section.columns.length}
      />
    );
  }

  return null;
}

function MissingInspector({ what }: { what: string }) {
  return (
    <aside className="emke-inspector">
      <div className="emke-section-title">Inspector</div>
      <div className="emke-empty">Selected {what} not found.</div>
    </aside>
  );
}

function WidgetInspectorPanel({ loc }: { loc: WidgetLocation }) {
  const deleteWidget = useEditor((s) => s.deleteWidget);
  const duplicateWidget = useEditor((s) => s.duplicateWidget);
  const moveWidget = useEditor((s) => s.moveWidget);
  const updateWidget = useEditor((s) => s.updateWidget);

  const { widget, widgetIndex, columnCount } = loc;
  const canMoveUp = widgetIndex > 0;
  const canMoveDown = widgetIndex < columnCount - 1;

  const InspectorComp = inspectorFor(widget.type);

  return (
    <aside className="emke-inspector">
      <div className="emke-section-title">
        Widget · <span style={{ color: "var(--accent)" }}>{widget.type}</span>
      </div>

      <div style={{ padding: "0 16px 8px", display: "flex", gap: 4 }}>
        <button
          type="button"
          className="emke-btn"
          onClick={() => moveWidget(widget.id, "up")}
          disabled={!canMoveUp}
          style={{ flex: 1, opacity: canMoveUp ? 1 : 0.4 }}
          title="Move up"
        >
          ↑
        </button>
        <button
          type="button"
          className="emke-btn"
          onClick={() => moveWidget(widget.id, "down")}
          disabled={!canMoveDown}
          style={{ flex: 1, opacity: canMoveDown ? 1 : 0.4 }}
          title="Move down"
        >
          ↓
        </button>
        <button
          type="button"
          className="emke-btn"
          onClick={() => duplicateWidget(widget.id)}
          style={{ flex: 1 }}
          title="Duplicate"
        >
          ⎘
        </button>
      </div>

      {InspectorComp ? (
        <InspectorComp widget={widget} />
      ) : (
        <div className="emke-empty">No inspector registered for "{widget.type}".</div>
      )}

      <AdvancedFields
        value={widget}
        onChange={(patch) => updateWidget(widget.id, patch)}
      />

      <div
        style={{
          padding: "16px",
          borderTop: "1px solid var(--border)",
          marginTop: 12,
        }}
      >
        <button
          type="button"
          className="emke-btn is-danger"
          onClick={() => deleteWidget(widget.id)}
          style={{ width: "100%" }}
        >
          Delete widget
        </button>
      </div>
    </aside>
  );
}
